import { SelectedSkillPills } from "@/components/selected-skill-pills";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ChatStatus } from "ai";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  Check,
  Copy,
  Zap,
  MessageSquareMore,
  MessagesSquare,
  Link2,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import type { AgnoChatMessage } from "@/hooks/use-agno-chat";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { isCanonical } from "@/lib/message-store";
import {
  useToolDetails,
  type ToolDetailsResolver,
} from "@/hooks/use-tool-details";
import { useCreatedTaskReferences } from "@/hooks/use-created-task-references";
import { useSubAgentReferences } from "@/hooks/use-sub-agent-references";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useIsKeyboardOpen } from "@/hooks/use-keyboard-open";
import { useConversationDeepLink } from "@/hooks/use-conversation-deep-link";
import { useConversationScrollSpy } from "@/hooks/use-conversation-scroll-spy";
import type { TimelineTurn } from "@/hooks/use-conversation-navigation";
import type { NavigationTargetLoadResult } from "@/hooks/use-session-message-feed";
import type { ConversationTarget } from "@/lib/conversation-target";
import type { MessageWindowCoverage } from "@/lib/message-window-store";
import type { Agent } from "@/hooks/use-agents";
import {
  useConversationScroll,
  type LoadOlderOptions,
} from "@/hooks/use-conversation-scroll";
import {
  attachThreadAnchors,
  buildRenderItems,
  type RenderItem,
} from "@/lib/render-items";
import type { CreatedTaskReference } from "@/lib/created-task";
import {
  pendingSubAgentSpawnFromToolCall,
  type SubAgentThreadReference,
} from "@/lib/sub-agent-thread";
import {
  threadAnchorDisplayFrom,
  type ThreadAnchorDisplay,
  type ThreadAnchorRef,
  type ThreadCreateRequest,
} from "@/lib/session";
import { cn } from "@/lib/utils";
import {
  conversationHeadingDomId,
  conversationMessageDomId,
} from "@/lib/conversation-headings";
import { canonicalConversationPermalink } from "@/lib/conversation-target";
import type { QuoteRange } from "@/lib/thread-quote-anchor";
import {
  Conversation,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent } from "@/components/ai-elements/message";
import { ParsedMessageContent } from "@/components/ai-elements/parsed-message";
import { ToolCallBadge } from "@/components/ai-elements/tool-call-badge";
import { workflowTaskIdFromToolResult } from "@/lib/workflow-run-links";
import { ToolGroupCollapsible } from "@/components/ai-elements/tool-group-collapsible";
import { VoiceTranscriptCard } from "@/components/ai-elements/voice-transcript-card";
import { MessageAttachments } from "@/components/ai-elements/message-attachments";
import { MessageMetaHover } from "@/components/ai-elements/message-meta-hover";
import { AssistantMessageTimestamp } from "@/components/ai-elements/message-timestamp";
import { TaskStatusEventBanner } from "@/components/ai-elements/task-status-event";
import { SubagentCompletionEventBanner } from "@/components/ai-elements/subagent-completion-event";
import { TaskCompletionEventsBanner } from "@/components/ai-elements/task-completion-events";
import { ApprovalCard } from "@/components/approval-card";
import { ThreadCreateButton } from "@/components/thread-create-button";
import { ThreadSelectionBar } from "@/components/thread-selection-bar";
import { SubAgentToolCard } from "@/components/sub-agent-tool-card";
import {
  parseTriggerMessage,
  TriggerMessageCard,
} from "@/components/trigger-message-card";
import {
  parseWorkflowTriggerMessage,
  WorkflowTriggerMessageCard,
} from "@/components/workflow-trigger-message-card";
import { Button } from "@/components/ui/button";
import { ComposerActionMessageCard } from "@/components/composer-action-message-card";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** Matches the parent-owned selection listener's current target. */
export interface ThreadSelectionTarget {
  messageId: string;
  sourceQuote: string;
  quoteRange?: QuoteRange | null;
  blockIndex: number;
  position: { top: number; left: number };
  barPosition?: { top: number } | null;
}

export function threadRequestForSelection(target: ThreadSelectionTarget): ThreadCreateRequest {
  return {
    source_message_id: target.messageId,
    source_quote: target.sourceQuote,
    ...(target.quoteRange ?? {}),
    anchor: {
      kind: "block",
      block_index: target.blockIndex,
      block_type: "paragraph",
    },
  };
}

/** Identifies the thread-creation control that currently owns the request. */
export interface ThreadCreationTarget {
  messageId: string;
  kind: "message" | "block";
}

/**
 * Thread creation is all-or-nothing for a list instance. Keeping the enabled
 * branch together prevents a row from receiving a callback without the
 * associated selection and pending-request state.
 */
export type ThreadCreation =
  | {
      enabled: true;
      creatingThread: boolean;
      threadCreationTarget: ThreadCreationTarget | null;
      threadSelection: ThreadSelectionTarget | null;
      onCreateMessageThread: (
        messageId: string,
        sourceQuote: string,
      ) => void | Promise<unknown>;
      onCreateSelectedTextThread: () => void | Promise<unknown>;
      onDismissSelectedTextThread: () => void;
    }
  | { enabled: false };

export interface ThreadActionRenderOptions {
  inline?: boolean;
  creatingThread?: boolean;
  threadCreationTarget?: ThreadCreationTarget | null;
}

export type RenderThreadAction = (
  messageId: string,
  sourceQuote: string,
  options?: ThreadActionRenderOptions,
) => ReactNode;

function CopyMessageAsMarkdownButton({
  sourceQuote,
  t,
}: {
  sourceQuote: string;
  t: TFunction;
}) {
  const { copied, copy } = useCopyToClipboard(2000);
  const Icon = copied ? Check : Copy;
  const label = copied
    ? t("threads.messageMarkdownCopied")
    : t("threads.copyMessageAsMarkdown");

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => {
              void copy(sourceQuote);
            }}
            className="inline-flex h-7 items-center gap-1.5 rounded-md pl-0.5 pr-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-60"
            aria-label={label}
            title={label}
            data-testid="copy-message-button"
            data-copied={copied || undefined}
          >
            <Icon className="size-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function MessagePermalinkControls({
  sessionId,
  messageId,
  t,
}: {
  sessionId: string;
  messageId: string;
  t: TFunction;
}) {
  const { copy } = useCopyToClipboard(2000);
  const permalink = typeof window === "undefined" ? null : canonicalConversationPermalink(
    window.location.origin,
    { sessionId, target: { kind: "message", messageId } },
  );
  if (!permalink) return null;
  const copyLink = () => {
    const link = canonicalConversationPermalink(window.location.origin, {
      sessionId,
      target: { kind: "message", messageId },
    });
    if (!link) return;
    void copy(link).then((copied) => {
      if (copied) toast.success(t("navigation.copyLinkSuccess"));
      else toast.error(t("navigation.copyLinkFailure"));
    });
  };
  return (
    <span className="inline-flex items-center">
      <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex h-7 items-center rounded-md px-1 text-muted-foreground transition-colors hover:text-foreground"
            aria-label={t("navigation.copyMessageLink")}
            onClick={() => copyLink()}
          >
            <Link2 className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("navigation.copyMessageLink")}</TooltipContent>
      </Tooltip>
      </TooltipProvider>
    </span>
  );
}

export interface MessageThreadActionsProps {
  sourceQuote: string;
  inline: boolean;
  isCreatingAssistantThread: boolean;
  threadCreationDisabled: boolean;
  onCreateMessageThread: () => void | Promise<unknown>;
  t: TFunction;
}

export function MessageThreadActions({
  sourceQuote,
  inline,
  isCreatingAssistantThread,
  threadCreationDisabled,
  onCreateMessageThread,
  t,
}: MessageThreadActionsProps) {
  return (
    <div className={cn(
      "flex items-center gap-2",
      inline ? "shrink-0 px-1" : "shrink-0",
    )}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => {
                void onCreateMessageThread();
              }}
              disabled={threadCreationDisabled}
              className="inline-flex h-7 items-center gap-1.5 rounded-md pl-2 pr-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-60"
              aria-label={t("threads.startThreadTooltip")}
              data-testid="start-thread-button"
            >
              {isCreatingAssistantThread ? (
                <Spinner className="size-3.5" />
              ) : (
                <MessageSquareMore className="size-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>{t("threads.startThreadTooltip")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <CopyMessageAsMarkdownButton sourceQuote={sourceQuote} t={t} />
    </div>
  );
}

export interface RenderToolOptions {
  /** Grouped tool calls render task pills after the group disclosure. */
  showCreatedTask?: boolean;
}

export type RenderTool = (
  toolItem: Extract<RenderItem, { kind: "tool" }>,
  options?: RenderToolOptions,
) => ReactNode;

/**
 * Stable identity for virtualized render rows.
 *
 * HITL marker messages are first shown optimistically with a client-generated
 * message id, then replaced by their persisted marker row on poll. A
 * requirement id is stable across that swap, so use it rather than the
 * transient message id to preserve the ApprovalCard (and its DOM-held secret
 * input) through reconciliation.
 */
export function getRenderItemKey(item: RenderItem, index: number): string {
  if (item.kind === "hitl") {
    return `hitl:${item.runId}:${item.requirements[0]?.id ?? index}`;
  }
  return item.id;
}

/**
 * Semantic identity for Virtua's prepend compensation.
 *
 * This deliberately covers the content which can affect a row's rendered
 * height, while excluding transport-only identity and volatile timestamps.
 * If any displayed content changes, the fingerprint changes and the scroll
 * hook conservatively leaves compensation to the normal render path.
 */
export function getRenderItemFingerprint(item: RenderItem): string {
  const fingerprint = (value: RenderItem): unknown => {
    switch (value.kind) {
      case "message":
        return {
          kind: value.kind,
          role: value.role,
          text: value.text,
          files: value.files.map(({ filename, mediaType, url, unshared }) => ({
            filename,
            mediaType,
            url,
            unshared,
          })),
          voiceTranscript: value.voiceTranscript,
          selectedSkills: value.selectedSkills,
          // The hover affordance changes the row layout; its timestamp does
          // not, so do not let a server-side timestamp normalization block a
          // history prepend from retaining its scroll position.
          // Assistant rows reserve space in their footer or bottom padding
          // before createdAt arrives, so timestamp text stays out of this fingerprint.
          userMeta: value.userMeta && {
            name: value.userMeta.name,
            elapsedSinceLastMsg: value.userMeta.elapsedSinceLastMsg,
            locale: value.userMeta.locale,
            localeName: value.userMeta.localeName,
          },
          scheduledRun: value.scheduledRun,
        };
      case "tool":
        return {
          kind: value.kind,
          name: value.name,
          status: value.status,
          source: value.source.kind === "live"
            ? { kind: value.source.kind, details: value.source.details }
            : { kind: value.source.kind },
        };
      case "tool-group":
        return {
          kind: value.kind,
          items: value.items.map(fingerprint),
          toolCount: value.toolCount,
          isStreaming: value.isStreaming,
          lastToolName: value.lastToolName,
          lastToolStatus: value.lastToolStatus,
        };
      case "hitl":
        return {
          kind: value.kind,
          runId: value.runId,
          requirements: value.requirements,
          resolved: value.resolved,
        };
      case "compaction":
        return {
          kind: value.kind,
          tokensBefore: value.tokensBefore,
          summaryLength: value.summaryLength,
        };
      case "thread-group":
        return {
          kind: value.kind,
          sourceMessageId: value.sourceMessageId,
          threads: value.threads,
        };
      case "task-status-event":
        return {
          kind: value.kind,
          event: {
            taskId: value.event.taskId,
            projectId: value.event.projectId,
            oldStatus: value.event.oldStatus,
            newStatus: value.event.newStatus,
            changedBy: value.event.changedBy,
          },
          text: value.text,
        };
      case "subagent-completion-event":
        return { kind: value.kind, event: value.event, text: value.text };
      case "task-completion-events":
        return { kind: value.kind, event: value.event, text: value.text };
      case "thinking":
      case "resuming":
        return { kind: value.kind };
    }
  };

  return JSON.stringify(fingerprint(item));
}

/**
 * Find the latest tool group in the active streaming segment.
 *
 * Assistant prose and the synthetic thinking row may trail an active group
 * while tokens arrive. Any other row ends that segment, preventing a group
 * from an earlier turn from auto-opening when a new run starts.
 */
export function getActiveToolGroupId(
  items: RenderItem[],
  streaming: boolean,
): string | null {
  if (!streaming) return null;

  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (item.kind === "tool-group") return item.id;
    if (item.kind === "message" && item.role === "assistant") continue;
    if (item.kind === "thinking") continue;
    return null;
  }

  return null;
}

/**
 * Render one virtualized conversation row.
 *
 * Values formerly captured by Conversation's item callback are explicit
 * props. This keeps the row deterministic and lets the list own only list
 * concerns such as virtual scrolling and pagination.
 */
export interface RenderItemRowProps {
  item: RenderItem;
  /** Raw createdAt from message metadata; absent for optimistic messages. */
  messageTimestamp?: unknown;
  isLastItem: boolean;
  /** Overall run lifecycle, independent of any per-item render marker. */
  isStreaming: boolean;
  /** True only for the latest tool group in the active streaming segment. */
  isActiveToolGroup: boolean;
  renderTool: RenderTool;
  /** Resolved task references keyed by tool render item id. */
  createdTaskFor?: (
    toolId: string,
  ) => CreatedTaskReference | null | undefined;
  /** Controlled group-disclosure state survives VList row remounts. */
  toolDetails: ToolDetailsResolver;
  onFileLinkClick?: (url: string, fileName: string) => void;
  t: TFunction;
  currentAgentId: string | null;
  sessionId: string;
  continueRun: (runId: string, tools: string) => Promise<void>;
  fetchWithAuth?: (url: string, init: RequestInit) => Promise<Response>;
  onOpenWorkflowRun?: (taskId: string) => void;
  /** Whether lifecycle/HITL actions are available to this caller. */
  canManageSession?: boolean;
  /** Suppress all mutation affordances, including HITL approval actions. */
  readOnly?: boolean;
  onOpenThread?: (
    childSessionId: string,
    threadAnchor?: ThreadAnchorDisplay | null,
    threadTitle?: string | null,
    agentId?: string | null,
  ) => void;
  threadAnchorDisplays: ReadonlyMap<string, ThreadAnchorDisplay>;
  threadAnchorRefs: ReadonlyMap<string, ThreadAnchorRef[]>;
  threadAgentIds: ReadonlyMap<string, string | null | undefined>;
  messageItemsById: ReadonlyMap<
    string,
    Extract<RenderItem, { kind: "message" }>
  >;
  threadedMessageIds: ReadonlySet<string>;
  renderThreadAction: RenderThreadAction;
  creatingThread: boolean;
  threadCreationTarget: ThreadCreationTarget | null;
  /** Suppresses every per-message thread action when creation is unavailable. */
  threadCreationEnabled: boolean;
  renderSurfaceId?: string;
}

export function RenderItemRow({
  item,
  messageTimestamp,
  isLastItem,
  isStreaming,
  isActiveToolGroup,
  renderTool,
  createdTaskFor,
  toolDetails,
  onFileLinkClick,
  t,
  currentAgentId,
  sessionId,
  continueRun,
  fetchWithAuth,
  onOpenWorkflowRun,
  canManageSession = true,
  readOnly = false,
  onOpenThread,
  threadAnchorDisplays,
  threadAnchorRefs,
  threadAgentIds,
  messageItemsById,
  threadedMessageIds,
  renderThreadAction,
  creatingThread,
  threadCreationTarget,
  threadCreationEnabled,
  renderSurfaceId = "standalone",
}: RenderItemRowProps) {
  const isStreamingLastItem = isLastItem && isStreaming;

  const renderEnabledThreadAction = (
    messageId: string,
    sourceQuote: string,
    options?: ThreadActionRenderOptions,
  ) => {
    if (!threadCreationEnabled || isStreamingLastItem) return null;
    return renderThreadAction(messageId, sourceQuote, {
      ...options,
      creatingThread,
      threadCreationTarget,
    });
  };

  if (item.kind === "task-status-event") {
    return <TaskStatusEventBanner event={item.event} />;
  }

  if (item.kind === "subagent-completion-event") {
    return <SubagentCompletionEventBanner event={item.event} />;
  }

  if (item.kind === "task-completion-events") {
    return <TaskCompletionEventsBanner event={item.event} />;
  }

  if (item.kind === "tool") {
    return (
      <div className="px-4 pb-4">
        <Message from="assistant">
          <MessageContent>{renderTool(item)}</MessageContent>
        </Message>
      </div>
    );
  }

  if (item.kind === "tool-group") {
    return (
      <div className="px-4 pb-4">
        <Message from="assistant">
          <MessageContent>
            <ToolGroupCollapsible
              item={item}
              runStreaming={isStreaming}
              open={toolDetails.isGroupOpen(
                item.id,
                isStreaming,
                isActiveToolGroup,
              )}
              onOpenChange={(open) => toolDetails.setGroupOpen(item.id, open)}
              showAll={toolDetails.isGroupShowAll(item.id)}
              onShowAllChange={(showAll) => {
                toolDetails.setGroupShowAll(item.id, showAll, true);
              }}
              renderTool={renderTool}
              createdTaskFor={createdTaskFor}
              onFileLinkClick={onFileLinkClick}
              renderSurfaceId={renderSurfaceId}
            />
          </MessageContent>
        </Message>
      </div>
    );
  }

  if (item.kind === "thinking") {
    return (
      <div className="px-4 pb-4">
        <Message from="assistant">
          <MessageContent>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Spinner className="size-4" />
              <span className="text-sm">{t("common:status.thinking")}</span>
            </div>
          </MessageContent>
        </Message>
      </div>
    );
  }

  if (item.kind === "resuming") {
    return (
      <div className="px-4 pb-4">
        <Message from="assistant">
          <MessageContent>
            <div className="flex items-center gap-2 text-destructive">
              <Spinner className="size-4" />
              <span className="text-sm">{t("common:status.resuming")}</span>
            </div>
          </MessageContent>
        </Message>
      </div>
    );
  }

  if (item.kind === "hitl") {
    if (readOnly) {
      return (
        <div className="px-4 pb-4">
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {t("approval.parentWorkflow")}
          </div>
        </div>
      );
    }
    return (
      <div className="px-4 pb-4">
        <ApprovalCard
          runId={item.runId}
          requirements={item.requirements}
          agentId={currentAgentId ?? ""}
          sessionId={sessionId}
          onContinue={continueRun}
          resolved={item.resolved}
          recoveryError={item.recoveryError}
          fetchWithAuth={fetchWithAuth}
          canResolve={canManageSession}
        />
      </div>
    );
  }

  if (item.kind === "compaction") {
    return (
      <div className="px-4 py-2">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex-1 border-t border-dashed border-muted" />
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <Package className="size-3.5" />
            {t("common:status.compacted")}{" · "}
            {t("compaction.tokenCount", { count: item.tokensBefore.toLocaleString() })}
          </span>
          <div className="flex-1 border-t border-dashed border-muted" />
        </div>
      </div>
    );
  }

  if (item.kind === "thread-group") {
    const sourceMessage = messageItemsById.get(item.sourceMessageId);
    const showThreadAction =
      sourceMessage?.role === "assistant"
      && sourceMessage.text.trim() !== "";

    return (
      <div className="flex flex-wrap items-center gap-2 px-4 py-1">
        {item.threads.map((thread) => (
          <button
            key={thread.childSessionId}
            type="button"
            onClick={() => {
              onOpenThread?.(
                thread.childSessionId,
                threadAnchorDisplays.get(thread.childSessionId),
                thread.threadTitle,
                threadAgentIds.get(thread.childSessionId),
              );
            }}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
            aria-label={t("threads.openThread")}
            data-testid="thread-indicator"
          >
            <MessagesSquare className="size-3.5" />
            <span>{thread.threadTitle || t("threads.thread")}</span>
          </button>
        ))}
        {showThreadAction && renderEnabledThreadAction(
          sourceMessage.id,
          sourceMessage.text.trim(),
          { inline: true },
        )}
      </div>
    );
  }

  const hasAssistantText = item.role === "assistant" && item.text.trim() !== "";
  const inlineThreadQuotes = (threadAnchorRefs.get(item.id) ?? []).map((ref) => ({
    childSessionId: ref.child_session_id,
    anchor: ref,
    title: ref.title,
    onOpen: () => onOpenThread?.(
      ref.child_session_id,
      threadAnchorDisplays.get(ref.child_session_id),
      ref.title,
      ref.agent_id,
    ),
  }));
  const hasAttachedThreads = threadedMessageIds.has(item.id);
  const assistantThreadAction = hasAssistantText && !hasAttachedThreads
    ? renderEnabledThreadAction(item.id, item.text.trim())
    : null;
  const legacyTriggerMessage = item.role === "user" ? parseTriggerMessage(item.text) : null;
  const triggerMessage = item.role === "user" && item.scheduledRun
    ? {
        trigger: item.scheduledRun.display_name || "",
        job: "",
        message: legacyTriggerMessage?.message ?? item.text,
        oneShot: false,
        plainText: true,
        meta: {
          firedAt: item.scheduledRun.firedAt,
          ...(item.scheduledRun.cron ? { schedule: item.scheduledRun.cron } : {}),
          ...(item.scheduledRun.timezone ? { timezone: item.scheduledRun.timezone } : {}),
        },
      }
    : legacyTriggerMessage;
  const workflowTriggerMessage = item.role === "user"
    ? parseWorkflowTriggerMessage(item.text)
    : null;
  const hasSpecialUserMessage = triggerMessage || workflowTriggerMessage || item.composerAction;
  return (
    <div
      id={conversationMessageDomId(renderSurfaceId, item.id)}
      data-message-id={item.id}
      data-conversation-message-anchor=""
      tabIndex={-1}
      className={cn("relative scroll-mt-16 px-4 outline-none focus-visible:ring-2", item.role === "assistant" && !assistantThreadAction ? "pb-5" : "pb-4")}
    >
      <Message from={item.role} className={item.composerAction ? "max-w-full ml-0" : undefined}>
        <MessageContent
          className={hasSpecialUserMessage
            ? "w-full group-[.is-user]:w-full group-[.is-user]:bg-transparent group-[.is-user]:px-0 group-[.is-user]:py-0"
            : undefined}
          {...(item.role === "assistant"
            ? {
                "data-assistant-message": "true",
                "data-message-id": item.id,
              }
            : {})}
        >
          {item.role === "assistant" ? (
            isLastItem && isStreaming && item.text.trim() === "" ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Spinner className="size-4" />
                <span className="text-sm">{t("common:status.thinking")}</span>
              </div>
            ) : (
              <>
                <ParsedMessageContent text={item.text} onFileLinkClick={onFileLinkClick} inlineThreadQuotes={inlineThreadQuotes} messageId={item.id} renderSurfaceId={renderSurfaceId} isStreaming={isLastItem && isStreaming} />
                {item.flashMode === true && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center text-muted-foreground" aria-label={t("chat:flash.served")}>
                          <Zap className="size-3.5" aria-hidden="true" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{t("chat:flash.served")}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {item.files.length > 0 && (
                  <MessageAttachments
                    files={item.files}
                    variant="pill"
                    className={item.text ? "mt-2" : undefined}
                    onFileClick={onFileLinkClick}
                  />
                )}
                {isLastItem && isStreaming && (
                  <div className="flex items-center gap-2 text-muted-foreground mt-2">
                    <Spinner className="size-4" />
                    <span className="text-sm">{t("common:status.thinking")}</span>
                  </div>
                )}
              </>
            )
          ) : (
            <>
              {item.voiceTranscript && (
                <VoiceTranscriptCard
                  transcript={item.voiceTranscript}
                  className={item.selectedSkills.length > 0 || item.files.length > 0 || item.text ? "mb-2" : undefined}
                />
              )}
              {item.selectedSkills.length > 0 && (
                <SelectedSkillPills
                  skills={item.selectedSkills}
                  className={item.files.length > 0 || item.text ? "mb-2" : undefined}
                />
              )}
              {item.files.length > 0 && (
                <MessageAttachments
                  files={item.files}
                  className={item.text ? "mb-2" : undefined}
                  onFileClick={onFileLinkClick}
                />
              )}
              {item.composerAction && fetchWithAuth ? (
                <ComposerActionMessageCard {...item.composerAction} sessionId={sessionId} fetchWithAuth={fetchWithAuth} onOpenRun={onOpenWorkflowRun} />
              ) : triggerMessage ? (
                <TriggerMessageCard
                  triggerMessage={triggerMessage}
                  onFileLinkClick={onFileLinkClick}
                />
              ) : workflowTriggerMessage ? (
                <WorkflowTriggerMessageCard
                  workflowMessage={workflowTriggerMessage}
                  onFileLinkClick={onFileLinkClick}
                />
              ) : (
                item.text && <ParsedMessageContent text={item.text} from="user" />
              )}
            </>
          )}
        </MessageContent>
        {item.role === "user" && (
          <div className="flex justify-end gap-1 pr-1">
            {item.userMeta && <MessageMetaHover userMeta={item.userMeta} />}
            <MessagePermalinkControls sessionId={sessionId} messageId={item.id} t={t} />
          </div>
        )}
        {item.role === "assistant" && (
          <div className={cn(
            "flex flex-wrap items-center gap-x-2 px-1",
            assistantThreadAction ? "min-h-7" : "absolute bottom-0 left-4 h-5",
          )}>
            <div className="flex h-5 items-center" data-testid="assistant-timestamp-slot">
              <AssistantMessageTimestamp timestamp={messageTimestamp} />
            </div>
            <MessagePermalinkControls sessionId={sessionId} messageId={item.id} t={t} />
            {assistantThreadAction}
          </div>
        )}
      </Message>
    </div>
  );
}

export interface ConversationMessageListProps {
  sessionId: string;
  messages: AgnoChatMessage[];
  status: ChatStatus;
  /** Recovery polling serializes tail merges with older-page prepends. */
  isPolling?: boolean;
  agentId: string | null;
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>;
  continueRun: (runId: string, tools: string) => Promise<void>;
  /** Defaults to true for callers that render known creator-owned sessions. */
  canManageSession?: boolean;
  /** Catalog used to resolve the target agent's display name on spawn cards. */
  allAgents?: readonly Agent[];
  /** Open a validated sub-agent reference in the parent-owned aside. */
  onOpenSubAgent?: (reference: SubAgentThreadReference) => void;
  onOpenWorkflowRun?: (taskId: string) => void;
  /** Publishes validated refs so URL reload can re-authorize an open aside. */
  onSubAgentReferencesResolved?: (
    references: readonly SubAgentThreadReference[],
  ) => void;
  /** Removes composer-adjacent actions and HITL controls from embedded feeds. */
  readOnly?: boolean;
  onFileLinkClick?: (url: string, fileName: string) => void;
  onOpenThread?: (
    childSessionId: string,
    threadAnchor?: ThreadAnchorDisplay | null,
    threadTitle?: string | null,
    agentId?: string | null,
  ) => void;
  threadAnchors: ThreadAnchorRef[];
  threadCreation: ThreadCreation;
  hasMore: boolean;
  totalMessages: number | null;
  isLoadingMore: boolean;
  olderPageError?: string | null;
  newerPageError?: string | null;
  /** True for the render transaction that commits an older page. */
  isPrepending: boolean;
  prependRevision: number;
  loadOlder: (options?: LoadOlderOptions) => Promise<void>;
  hasLoaded: boolean;
  error: string | null;
  retry: () => void;
  loadNavigationTarget?: (
    target: ConversationTarget,
    signal: AbortSignal,
  ) => Promise<NavigationTargetLoadResult>;
  hasNewerWindowActivity?: boolean;
  jumpToLatest?: () => void;
  navigationCoverage?: MessageWindowCoverage | null;
  navigationContentMessageId?: string | null;
  navigationContentError?: boolean;
  continueNavigationContent?: (messageId: string) => void | Promise<void>;
  cancelNavigationContent?: () => void;
  isLoadingNewerNavigation?: boolean;
  loadNewerNavigationMessages?: () => void | Promise<void>;
  /** A trusted interaction with the rendered transcript. */
  onUserInteraction?: (event: Event) => void;
  scrollSpy?: {
    enabled: boolean;
    turns: TimelineTurn[];
    onVisibleTargetChange: (target: ConversationTarget) => void;
  };
  className?: string;
  /**
   * Optional content shown before a populated conversation, or as a complete
   * replacement for the zero-row state. This lets embedded views add context
   * such as a thread-origin banner without maintaining a second renderer.
   */
  leadingContent?: ReactNode;
  /** Optional purpose-built replacement for the normal empty conversation. */
  emptyContent?: ReactNode;
}

const unsupportedNavigationTarget = async (): Promise<NavigationTargetLoadResult> => ({
  state: "unsupported",
});

export interface OlderHistoryControlProps {
  olderMessagesRemaining: number | null;
  isLoadingMore: boolean;
  error?: string | null;
  isStreaming: boolean;
  isPolling?: boolean;
  onForceLoadOlder: () => void;
  t: TFunction;
}

/** Older-page status and explicit retry, sharing the automatic loader. */
export function OlderHistoryControl({
  olderMessagesRemaining,
  isLoadingMore,
  error = null,
  isStreaming,
  isPolling = false,
  onForceLoadOlder,
  t,
}: OlderHistoryControlProps) {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 border-b bg-background/90 px-4 py-1.5 text-xs text-muted-foreground backdrop-blur-sm"
      role="status"
      aria-busy={isLoadingMore}
    >
      <span>
        {error ?? (olderMessagesRemaining !== null
          ? t("common:status.olderMessagesRemaining", { count: olderMessagesRemaining })
          : t("common:status.olderMessagesAvailable"))}
      </span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="pointer-events-auto"
        disabled={isLoadingMore || (isStreaming && !isPolling)}
        onClick={onForceLoadOlder}
      >
        {isLoadingMore && <Spinner className="size-4" aria-hidden="true" />}
        {isLoadingMore
          ? t("common:status.loadingMessages")
          : error ? t("common:actions.retry") : t("common:actions.loadOlderMessages")}
      </Button>
    </div>
  );
}

export function NewerHistoryControl({
  coverage,
  isLoading,
  error,
  loadNewer,
  t,
}: {
  coverage: MessageWindowCoverage;
  isLoading: boolean;
  error: string | null;
  loadNewer?: () => void | Promise<void>;
  t: TFunction;
}) {
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-3 border-t border-dashed bg-muted/50 px-4 py-1.5 text-xs text-muted-foreground"
      role="status"
      aria-busy={isLoading}
      data-testid="navigation-gap-after"
    >
      <span>
        {error ?? (coverage.partialTurn && !coverage.endsAtTurnBoundary
          ? t("navigation.partialCoverageAfter")
          : t("navigation.newerCoverageAvailable"))}
      </span>
      {loadNewer && coverage.afterCursor && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={isLoading}
          onClick={() => { void loadNewer(); }}
        >
          {isLoading && <Spinner className="size-4" aria-hidden="true" />}
          {isLoading ? t("common:status.loadingMessages")
            : error ? t("common:actions.retry") : t("navigation.loadNewer")}
        </Button>
      )}
    </div>
  );
}

/**
 * The visual, virtualized half of a conversation.
 *
 * Fetching, pagination cursors, and text-selection listeners remain parent
 * concerns. This component owns construction of visual rows, disclosure and
 * empty-state UI, and thread controls. `useConversationScroll` owns the full
 * virtualizer lifecycle and exposes only viewport bindings and domain commands.
 */
export function ConversationMessageList({
  sessionId,
  messages,
  status,
  isPolling = false,
  agentId,
  fetchWithAuth,
  continueRun,
  canManageSession = true,
  allAgents = [],
  onOpenSubAgent,
  onOpenWorkflowRun,
  onSubAgentReferencesResolved,
  readOnly = false,
  onFileLinkClick,
  onOpenThread,
  threadAnchors,
  threadCreation,
  hasMore,
  totalMessages,
  isLoadingMore,
  olderPageError = null,
  newerPageError = null,
  isPrepending,
  prependRevision,
  loadOlder,
  hasLoaded,
  error,
  retry,
  loadNavigationTarget = unsupportedNavigationTarget,
  hasNewerWindowActivity = false,
  jumpToLatest,
  navigationCoverage = null,
  navigationContentMessageId = null,
  navigationContentError = false,
  continueNavigationContent,
  cancelNavigationContent = () => {},
  isLoadingNewerNavigation = false,
  loadNewerNavigationMessages,
  onUserInteraction,
  scrollSpy,
  className,
  leadingContent,
  emptyContent,
}: ConversationMessageListProps) {
  const { t } = useTranslation();
  const scrollSpyRootRef = useRef<HTMLDivElement>(null);
  const renderSurfaceId = useId();
  const isTouchPointer = useMediaQuery("(pointer: coarse) and (hover: none)");
  const isKeyboardOpen = useIsKeyboardOpen();
  const baseRenderItems = useMemo(
    () => buildRenderItems(messages, status, {
      incompleteEnd: navigationCoverage?.partialTurn === true
        && navigationCoverage.endsAtTurnBoundary === false,
    }),
    [messages, navigationCoverage, status],
  );
  const renderItems = useMemo(
    () => attachThreadAnchors(baseRenderItems, threadAnchors),
    [baseRenderItems, threadAnchors],
  );
  const isStreaming = status === "submitted" || status === "streaming";
  const activeToolGroupId = useMemo(
    () => getActiveToolGroupId(baseRenderItems, isStreaming),
    [baseRenderItems, isStreaming],
  );
  const loadedCanonicalCount = useMemo(
    () => messages.reduce((count, message) => count + (isCanonical(message) ? 1 : 0), 0),
    [messages],
  );
  const partialNavigationMessage = useMemo(
    () => messages.find((message) => (
      message.metadata?.navigationPartialContent === true
      && typeof message.metadata?.navigationContentCursor === "string"
    )) ?? null,
    [messages],
  );
  const tailContentKey = useMemo(() => {
    const tail = renderItems[renderItems.length - 1];
    // The streamed text itself changes the scroll dependency on every frame;
    // serializing the whole growing row allocates another full copy each time.
    if (isStreaming && tail?.kind === "message") return tail.text;
    return tail ? JSON.stringify(tail) ?? tail.id : "";
  }, [isStreaming, renderItems]);
  const threadAnchorDisplays = useMemo(
    () => new Map(
      threadAnchors.flatMap((anchor) => {
        const displayAnchor = threadAnchorDisplayFrom(anchor);
        return displayAnchor
          ? [[anchor.child_session_id, displayAnchor] as const]
          : [];
      }),
    ),
    [threadAnchors],
  );
  const threadAnchorRefs = useMemo(() => {
    const byMessage = new Map<string, ThreadAnchorRef[]>();
    for (const ref of threadAnchors) {
      const refs = byMessage.get(ref.source_message_id) ?? [];
      refs.push(ref);
      byMessage.set(ref.source_message_id, refs);
    }
    return byMessage;
  }, [threadAnchors]);
  const threadAgentIds = useMemo(
    () => new Map(
      threadAnchors.map((anchor) => [anchor.child_session_id, anchor.agent_id]),
    ),
    [threadAnchors],
  );
  const threadedMessageIds = useMemo(
    () => new Set(threadAnchors.map((anchor) => anchor.source_message_id)),
    [threadAnchors],
  );
  const messageItemsById = useMemo(() => {
    const itemsById = new Map<string, Extract<RenderItem, { kind: "message" }>>();
    for (const item of baseRenderItems) {
      if (item.kind === "message") itemsById.set(item.id, item);
    }
    return itemsById;
  }, [baseRenderItems]);
  const messageTimestampsById = useMemo(() => {
    const timestampsById = new Map<string, unknown>();
    for (const message of messages) {
      timestampsById.set(message.id, message.metadata?.createdAt);
    }
    return timestampsById;
  }, [messages]);

  const toolDetails = useToolDetails(sessionId, fetchWithAuth);
  const deepLink = useConversationDeepLink({
    sessionId,
    messages,
    threadAnchors,
    hasMore,
    loadOlder,
    hasLoaded,
    loadNavigationTarget,
  });
  const armScrollSpy = useConversationScrollSpy({
    rootRef: scrollSpyRootRef,
    enabled: scrollSpy?.enabled ?? false,
    turns: scrollSpy?.turns ?? [],
    navigationRequestId: deepLink.requestId,
    onVisibleTargetChange: scrollSpy?.onVisibleTargetChange ?? (() => {}),
  });
  const targetLocation = useMemo(() => {
    if (!deepLink.targetMessageId) return { rowIndex: null, groupId: null };
    for (let index = 0; index < renderItems.length; index += 1) {
      const item = renderItems[index];
      if (item.kind === "message" && item.id === deepLink.targetMessageId) {
        return { rowIndex: index, groupId: null };
      }
      if (
        item.kind === "tool-group"
        && item.items.some((inner) => inner.kind === "message" && inner.id === deepLink.targetMessageId)
      ) {
        return { rowIndex: index, groupId: item.id };
      }
    }
    return { rowIndex: null, groupId: null };
  }, [deepLink.targetMessageId, renderItems]);
  useEffect(() => {
    if (targetLocation.groupId) toolDetails.setGroupOpen(targetLocation.groupId, true);
  }, [targetLocation.groupId, toolDetails.setGroupOpen]);
  const targetHeadingKey = deepLink.target?.kind === "message"
    ? deepLink.target.headingKey
    : undefined;
  const targetElementId = deepLink.targetMessageId
    ? targetHeadingKey
      ? conversationHeadingDomId(renderSurfaceId, deepLink.targetMessageId, targetHeadingKey)
      : conversationMessageDomId(renderSurfaceId, deepLink.targetMessageId)
    : null;
  const [headingChanged, setHeadingChanged] = useState(false);
  useEffect(() => {
    setHeadingChanged(false);
    if (deepLink.status !== "ready" || !targetHeadingKey || !deepLink.targetMessageId) return;
    const timer = window.setTimeout(() => {
      if (!document.getElementById(targetElementId!)) setHeadingChanged(true);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [deepLink.requestId, deepLink.status, deepLink.targetMessageId, targetElementId, targetHeadingKey]);
  useEffect(() => {
    // Closed groups do not mount their badges. Resolve older workflow rows
    // from the mounted conversation list so opening a group has a ready link.
    for (const item of baseRenderItems) {
      const tools = item.kind === "tool-group" ? item.items : [item];
      for (const tool of tools) {
        if (tool.kind === "tool" && !workflowTaskIdFromToolResult(
          tool.name, tool.status, null, tool.workflowTaskId,
        )) {
          toolDetails.preloadWorkflowRun(tool.source, tool.name);
        }
      }
    }
  }, [baseRenderItems, sessionId, toolDetails.preloadWorkflowRun]);
  const createdTaskReferences = useCreatedTaskReferences(
    sessionId,
    baseRenderItems,
    fetchWithAuth,
  );
  const subAgentReferences = useSubAgentReferences(
    sessionId,
    baseRenderItems,
    fetchWithAuth,
  );
  const createdTaskFor = useCallback(
    (toolId: string) => createdTaskReferences.get(toolId),
    [createdTaskReferences],
  );
  const subAgentFor = useCallback(
    (toolId: string) => subAgentReferences.get(toolId),
    [subAgentReferences],
  );
  useEffect(() => {
    onSubAgentReferencesResolved?.([...subAgentReferences.values()]);
  }, [onSubAgentReferencesResolved, subAgentReferences]);

  const renderTool = useCallback<RenderTool>((
    toolItem,
    { showCreatedTask = true } = {},
  ) => {
    const subAgent = subAgentFor(toolItem.id);
    if (subAgent) {
      return (
        <SubAgentToolCard
          reference={subAgent}
          allAgents={allAgents}
          fetchWithAuth={fetchWithAuth}
          onOpen={onOpenSubAgent ?? (() => {})}
        />
      );
    }
    const pendingSubAgent = toolItem.source.kind === "live"
      && toolItem.source.details.parameters.state === "available"
      ? pendingSubAgentSpawnFromToolCall(
          toolItem.name,
          toolItem.status,
          toolItem.source.details.parameters.value,
        )
      : null;
    if (pendingSubAgent) {
      return (
        <SubAgentToolCard
          pending={pendingSubAgent}
          allAgents={allAgents}
        />
      );
    }
    return <ToolCallBadge
      id={toolItem.id}
      status={toolItem.status}
      name={toolItem.name}
      source={toolItem.source}
      open={toolDetails.isExpanded(toolItem.source)}
      historicalState={toolDetails.stateFor(toolItem.source)}
      onOpenChange={(open) => toolDetails.setOpen(toolItem.source, open)}
      onRetry={() => toolDetails.retry(toolItem.source)}
      onPreloadWorkflowRun={toolDetails.preloadWorkflowRun}
      workflowTaskId={toolItem.workflowTaskId}
      createdTask={showCreatedTask ? createdTaskFor(toolItem.id) : undefined}
      onOpenRun={onOpenWorkflowRun}
    />;
  }, [
    allAgents,
    createdTaskFor,
    fetchWithAuth,
    onOpenSubAgent,
    onOpenWorkflowRun,
    subAgentFor,
    toolDetails,
  ]);

  const enabledThreadCreation = threadCreation.enabled ? threadCreation : null;
  const renderThreadAction = useCallback<RenderThreadAction>((
    messageId,
    sourceQuote,
    options = {},
  ) => {
    if (!enabledThreadCreation) return null;

    const {
      inline = false,
      creatingThread: actionCreatingThread = enabledThreadCreation.creatingThread,
      threadCreationTarget: actionCreationTarget = enabledThreadCreation.threadCreationTarget,
    } = options;
    const isCreatingAssistantThread =
      actionCreationTarget?.kind === "message"
      && actionCreationTarget.messageId === messageId;
    const threadCreationDisabled =
      actionCreatingThread || actionCreationTarget !== null;
    return (
      <MessageThreadActions
        sourceQuote={sourceQuote}
        inline={inline}
        isCreatingAssistantThread={isCreatingAssistantThread}
        threadCreationDisabled={threadCreationDisabled}
        onCreateMessageThread={() => (
          enabledThreadCreation.onCreateMessageThread(messageId, sourceQuote)
        )}
        t={t}
      />
    );
  }, [enabledThreadCreation, t]);

  const scrollController = useConversationScroll({
    sessionId,
    items: renderItems,
    getItemKey: getRenderItemKey,
    getItemFingerprint: getRenderItemFingerprint,
    tailContentKey,
    status,
    isPolling,
    hasLoaded,
    hasMore,
    totalMessages,
    loadedMessageCount: loadedCanonicalCount,
    isLoadingOlder: isLoadingMore,
    olderPageError,
    newerCursor: navigationCoverage?.hasNewer ? navigationCoverage.afterCursor : null,
    isLoadingNewer: isLoadingNewerNavigation,
    newerPageError,
    loadNewer: loadNewerNavigationMessages,
    isPrepending,
    prependRevision,
    loadOlder,
    navigation: deepLink.target && !["invalid", "missing", "cancelled"].includes(deepLink.status)
      ? {
          requestId: deepLink.requestId,
          rowIndex: targetLocation.rowIndex,
          elementId: headingChanged && deepLink.targetMessageId
            ? conversationMessageDomId(renderSurfaceId, deepLink.targetMessageId)
            : targetElementId,
        }
      : null,
  });
  const hasRenderItems = renderItems.length > 0;

  return (
    <div ref={scrollSpyRootRef} className={cn("relative flex min-h-0 min-w-0 flex-1 flex-col", className)}>
      {hasRenderItems ? (
        <>
          {leadingContent}
          <div className="relative flex min-h-0 flex-1 flex-col">
            {deepLink.status !== "idle" && deepLink.status !== "ready" && (
              <div
                className="absolute inset-x-4 top-2 z-20 flex items-center justify-between gap-3 rounded-md border bg-background/95 px-3 py-2 text-sm shadow-sm"
                role={deepLink.status === "invalid" || deepLink.status === "missing" ? "alert" : "status"}
                aria-live="polite"
                data-testid="conversation-navigation-status"
              >
                <span>
                  {deepLink.status === "invalid"
                    ? t("navigation.invalidLink")
                    : deepLink.status === "missing"
                      ? t("navigation.targetUnavailable")
                      : deepLink.status === "retryable-error"
                        ? t("navigation.targetRetryableError")
                      : deepLink.status === "paused"
                        ? t("navigation.searchPaused", { count: deepLink.searchedPages })
                        : deepLink.status === "cancelled"
                          ? t("navigation.searchCancelled")
                          : t("navigation.locatingTarget", { count: deepLink.searchedPages })}
                </span>
                <div className="flex shrink-0 gap-1">
                  {deepLink.status === "paused" && (
                    <Button type="button" size="sm" variant="outline" onClick={deepLink.continueSearch}>
                      {t("navigation.continueSearch")}
                    </Button>
                  )}
                  {deepLink.status === "retryable-error" && (
                    <Button type="button" size="sm" variant="outline" onClick={deepLink.retry}>
                      {t("navigation.retryTarget")}
                    </Button>
                  )}
                  {(deepLink.status === "locating" || deepLink.status === "paused" || deepLink.status === "retryable-error") && (
                    <Button type="button" size="sm" variant="ghost" onClick={deepLink.cancel}>
                      {t("navigation.cancel")}
                    </Button>
                  )}
                </div>
              </div>
            )}
            {headingChanged && (
              <div className="absolute inset-x-4 top-2 z-20 rounded-md border bg-background/95 px-3 py-2 text-sm shadow-sm" role="status">
                {t("navigation.headingChanged")}
              </div>
            )}
            {scrollController.showOlderHistoryControl && (
              <OlderHistoryControl
                olderMessagesRemaining={scrollController.olderMessagesRemaining}
                isLoadingMore={isLoadingMore}
                error={olderPageError}
                isStreaming={isStreaming}
                isPolling={isPolling}
                onForceLoadOlder={() => {
                  void scrollController.commands.forceLoadOlder();
                }}
                t={t}
              />
            )}
            {navigationCoverage?.partialTurn && !navigationCoverage.startsAtTurnBoundary && (
              <div
                className="shrink-0 border-b border-dashed bg-muted/50 px-4 py-1.5 text-center text-xs text-muted-foreground"
                role="status"
                data-testid="navigation-gap-before"
              >
                {t("navigation.partialCoverageBefore")}
              </div>
            )}
            <Conversation<RenderItem>
              key={sessionId}
              className={cn(
                "min-h-0 flex-1",
                scrollController.isPositioned
                  ? undefined
                  : "pointer-events-none opacity-0",
              )}
              data={renderItems}
              getItemKey={getRenderItemKey}
              onUserInteraction={(event) => { armScrollSpy(event); onUserInteraction?.(event); }}
              {...scrollController.viewportBindings}
            >
              {(item: RenderItem) => (
                <RenderItemRow
                  item={item}
                  messageTimestamp={messageTimestampsById.get(item.id)}
                  isLastItem={
                    renderItems.length > 0
                    && item.id === renderItems[renderItems.length - 1].id
                  }
                  isStreaming={isStreaming}
                  isActiveToolGroup={
                    item.kind === "tool-group"
                    && item.id === activeToolGroupId
                  }
                  renderTool={renderTool}
                  createdTaskFor={createdTaskFor}
                  toolDetails={toolDetails}
                  onFileLinkClick={onFileLinkClick}
                  t={t}
                  currentAgentId={agentId}
                  sessionId={sessionId}
                  continueRun={continueRun}
                  fetchWithAuth={fetchWithAuth}
                  onOpenWorkflowRun={onOpenWorkflowRun}
                  canManageSession={canManageSession}
                  readOnly={readOnly}
                  onOpenThread={onOpenThread}
                  threadAnchorDisplays={threadAnchorDisplays}
                  threadAnchorRefs={threadAnchorRefs}
                  threadAgentIds={threadAgentIds}
                  messageItemsById={messageItemsById}
                  threadedMessageIds={threadedMessageIds}
                  renderThreadAction={renderThreadAction}
                  creatingThread={enabledThreadCreation?.creatingThread ?? false}
                  threadCreationTarget={
                    enabledThreadCreation?.threadCreationTarget ?? null
                  }
                  threadCreationEnabled={enabledThreadCreation !== null}
                  renderSurfaceId={renderSurfaceId}
                />
              )}
            </Conversation>
            {navigationCoverage?.hasNewer && (
              <NewerHistoryControl
                coverage={navigationCoverage}
                isLoading={isLoadingNewerNavigation}
                error={newerPageError}
                loadNewer={loadNewerNavigationMessages}
                t={t}
              />
            )}
            {partialNavigationMessage && continueNavigationContent && (
              <div
                className="flex shrink-0 items-center justify-between gap-3 border-t bg-background px-4 py-2 text-xs text-muted-foreground"
                role={navigationContentError ? "alert" : "status"}
                data-testid="navigation-partial-content"
              >
                <span>
                  {navigationContentError
                    ? t("navigation.partialContentError")
                    : t("navigation.partialContent")}
                </span>
                <div className="flex gap-1">
                  {navigationContentMessageId === partialNavigationMessage.id ? (
                    <Button type="button" size="sm" variant="ghost" onClick={cancelNavigationContent}>
                      {t("navigation.cancel")}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => { void continueNavigationContent(partialNavigationMessage.id); }}
                    >
                      {t("navigation.continueContent")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      ) : leadingContent ?? (!hasLoaded && emptyContent ? emptyContent : !hasLoaded ? (
        <div
          className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-muted-foreground"
          data-testid="messages-loading"
          aria-busy="true"
          aria-live="polite"
        >
          <Spinner className="size-5" />
          <p className="text-sm">{t("common:status.loadingMessages")}</p>
        </div>
      ) : error ? (
        <div
          className="flex h-full flex-col items-center justify-center space-y-3 p-4 text-center"
          data-testid="messages-error"
          role="alert"
          aria-live="assertive"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/60">
            <AlertCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {t("common:errors.messageLoadError")}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          </div>
          <Button onClick={retry}>{t("common:actions.retry")}</Button>
        </div>
      ) : emptyContent ?? (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-muted-foreground">
          <p className="text-lg font-medium">{t("common:errors.noMessages")}</p>
          <p className="text-sm">{t("common:errors.noMessagesHint")}</p>
        </div>
      ))}

      {enabledThreadCreation?.threadSelection && (isTouchPointer ? (
        !isKeyboardOpen && <ThreadSelectionBar
          sourceQuote={enabledThreadCreation.threadSelection.sourceQuote}
          position={enabledThreadCreation.threadSelection.barPosition}
          label={t("threads.startThread")}
          dismissLabel={t("threads.dismissSelection")}
          creating={
            enabledThreadCreation.threadCreationTarget?.kind === "block"
            && enabledThreadCreation.threadCreationTarget.messageId
              === enabledThreadCreation.threadSelection.messageId
          }
          disabled={
            enabledThreadCreation.creatingThread
            || enabledThreadCreation.threadCreationTarget !== null
          }
          onClick={() => {
            void enabledThreadCreation.onCreateSelectedTextThread();
          }}
          onDismiss={enabledThreadCreation.onDismissSelectedTextThread}
        />
      ) : (
        <ThreadCreateButton
          position={enabledThreadCreation.threadSelection.position}
          label={t("threads.startThread")}
          creating={
            enabledThreadCreation.threadCreationTarget?.kind === "block"
            && enabledThreadCreation.threadCreationTarget.messageId
              === enabledThreadCreation.threadSelection.messageId
          }
          disabled={
            enabledThreadCreation.creatingThread
            || enabledThreadCreation.threadCreationTarget !== null
          }
          onClick={() => {
            void enabledThreadCreation.onCreateSelectedTextThread();
          }}
        />
      ))}

      <ConversationScrollButton
        isAtBottom={scrollController.isAtBottom && !hasNewerWindowActivity}
        hasMessages={messages.length > 0}
        onScrollToBottom={() => {
          if (scrollController.isAtBottom && hasNewerWindowActivity && !navigationCoverage?.hasNewer) {
            jumpToLatest?.();
          }
          scrollController.commands.stickToBottom(!isStreaming);
        }}
      />
    </div>
  );
}
